import classNames from "classnames";
import React, { ChangeEvent, useCallback, PropsWithChildren } from "react";
import { v4 as uuid } from "uuid";

import Icon from "../Icon";
import { IconSlug } from "../Icon";
import InputLabel from "../InputLabel";
import "./styles.scss";

export type SelectProps<T extends string> = PropsWithChildren<{
  id?: string;
  label: string;
  value?: T;
  onChange?: (selection: T) => void;
  horizontal?: boolean;
  size?: "small" | "medium" | "large";
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  className?: string;
  icon?: IconSlug;
  variant?: "ghost";
}>;

export default function Select<T extends string>({
  children,
  id = uuid(),
  label,
  onChange,
  value,
  horizontal,
  size = "large",
  required,
  disabled,
  hideLabel,
  className,
  icon,
  variant,
}: SelectProps<T>) {
  const selectClassName = classNames({
    "c-select": true,
    "has-label": label,
    "is-horizontal": horizontal,
    [`is-${size}`]: size,
    [className ?? ""]: className,
  });

  const handleChange = useCallback(
    (ev: ChangeEvent<HTMLSelectElement>) => {
      if (onChange) onChange(ev.currentTarget.value as T);
    },
    [onChange],
  );

  return (
    <>
      <div className={selectClassName}>
        <InputLabel className="c-select__label" visuallyHidden={hideLabel} htmlFor={id}>
          {label}
        </InputLabel>

        <div className="c-select__wrapper">
          {icon && <Icon className="c-select__pre-icon" icon={icon} />}
          <select
            id={id}
            className={`c-select__input  ${variant ? "is-" + variant : ""}`}
            value={value ?? ""}
            onChange={handleChange}
            required={required}
            disabled={disabled}
          >
            {children}
          </select>
          <Icon className="c-select__icon" icon="chevronDown" />
        </div>
      </div>
    </>
  );
}
